/**
 * ErrorDisplay - Presentation component for displaying errors
 * Follows ADR-004: Separation of Concerns - Pure presentation component
 */

import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Props for ErrorDisplay component
 */
interface ErrorDisplayProps {
  /** Error message to display */
  message: string;
  /** Callback when error should be dismissed */
  onDismiss: () => void;
  /** Optional callback for retry action */
  onRetry?: () => void;
  /** Label for retry button (default: 'Retry') */
  retryLabel?: string;
}

/**
 * ErrorDisplay - Pure presentation component for error messages
 * 
 * @example
 * ```tsx
 * <ErrorDisplay
 *   message="Failed to connect"
 *   onDismiss={() => setError('')}
 *   onRetry={() => retryConnection()}
 *   retryLabel="Try Again"
 * />
 * ```
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ 
  message, 
  onDismiss,
  onRetry,
  retryLabel = 'Retry'
}) => {
  return (
    <div className="chatbot-error-display bg-destructive/10 border-b border-destructive/30 px-3 sm:px-6 py-2 sm:py-3">
      <div className="flex items-center space-x-2">
        <AlertCircle size={14} className="chatbot-error-icon sm:w-4 sm:h-4 text-destructive flex-shrink-0" />
        <span className="chatbot-error-text text-destructive text-xs sm:text-sm flex-1">{message}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="chatbot-error-retry-button flex items-center gap-1 px-2 py-1 text-xs font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded transition-colors flex-shrink-0"
          >
            <RefreshCw size={12} />
            {retryLabel}
          </button>
        )}
        {!onRetry && (
          <button
            onClick={onDismiss}
            className="chatbot-error-dismiss-button ml-auto text-destructive hover:text-destructive/70 flex-shrink-0 text-lg leading-none"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

